import React, { useState, useEffect } from 'react';
import { CalendarIcon, UserIcon, EnvelopeIcon, PhoneIcon, MapPinIcon, CheckIcon, XMarkIcon, BuildingOfficeIcon, InformationCircleIcon, LockClosedIcon } from "@heroicons/react/24/outline";
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import ConfirmationCode from 'components/ConfirmationCode/ConfirmationCode';
import { countdown } from 'utils/helpers';

const TabAthena = () => {
    const navigate = useNavigate();
    const [userProfile, setUserProfile] = useState({
        name: '',
        surname: '',
        dob: '',
        phone: '',
        about: '',
        location: {
            addressLine1: '',
            addressLine2: '',
            city: '',
            state: '',
            zipCode: '',
        }
    });
    const [athenaProfile, setAthenaProfile] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const [showOtpModal, setShowOtpModal] = useState(false);
    const [otp, setOtp] = useState('');
    const [timeLeft, setTimeLeft] = useState(0);
    const [isOTPSend, setIsOTPSend] = useState(false);
    const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
    const [showSupportModal, setShowSupportModal] = useState(false);
    const supportPhoneNumber = "+1 (617) 237-0589";

    useEffect(() => {
        // Fetch user profile data, and Athena patient data
        Promise.all([
            axios.get('/profile/general'),
            axios.get('/athena/profile'),
            axios.get('/athena/patient/info').catch(err => {
                // If not registered with Athena yet, this is expected to fail
                if (err.response?.status === 404) {
                    return { data: null };
                }
                // For other errors, log them but don't prevent registration
                console.warn('Error fetching Athena patient info:', err.message);
                return { data: null };
            })
        ])
            .then(([profileRes, athenaProfileRes, athenaRes]) => {

                setUserProfile(profileRes.data);
                setAthenaProfile(athenaProfileRes.data);
                // Athena returns an array with a single patient object, or null if not registered
                // setAthenaProfile(athenaRes?.data?.[0] || null);
            })
            .catch((error) => {
                console.error("Error fetching data:", error);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, []);

    const requiredFields = [
        {
            key: 'name',
            label: 'First Name',
            icon: UserIcon,
            value: userProfile.name,
            required: true
        },
        {
            key: 'surname',
            label: 'Last Name',
            icon: UserIcon,
            value: userProfile.surname,
            required: true
        },
        {
            key: 'dob',
            label: 'Date of Birth',
            icon: CalendarIcon,
            value: userProfile.dob,
            required: true
        },
        {
            key: 'email',
            label: 'Email',
            icon: EnvelopeIcon,
            value: userProfile.email,
            required: true
        },
        {
            key: 'phone',
            label: 'Phone Number',
            icon: PhoneIcon,
            value: userProfile.phone,
            required: true
        },
        {
            key: 'city',
            label: 'City',
            icon: MapPinIcon,
            value: userProfile.location?.city,
            required: true
        },
        {
            key: 'state',
            label: 'State',
            icon: MapPinIcon,
            value: userProfile.location?.state,
            required: true
        },
        {
            key: 'zipCode',
            label: 'Zip Code',
            icon: MapPinIcon,
            value: userProfile.location?.zipCode,
            required: true
        }
    ];

    const isFieldComplete = (field) => {
        return field.value && field.value.trim() !== '';
    };

    const isAllRequiredFieldsComplete = () => {
        return requiredFields
            .filter(field => field.required)
            .every(field => isFieldComplete(field));
    };

    const handleAthenaResponse = (response) => {
        if (response.data && typeof response.data.mergeRequest !== 'undefined') {
            if (response.data.mergeRequest === true) {
                setShowOtpModal(true);
                return true;
            } else if (response.data.mergeRequest === false) {
                setShowSupportModal(true);
                return true;
            }
        }
        return false;
    };

    const handleRegisterWithAthena = async () => {
        // Check if user has completed their profile
        if (!userProfile.name || !userProfile.surname || !userProfile.dob || !userProfile.email || !userProfile.phone) {
            setErrorMessage('Please complete your profile information in the General tab before registering with Athena Health.');
            return;
        }

        // Check if user has completed their address
        if (!userProfile.location?.city || !userProfile.location?.state || !userProfile.location?.zipCode) {
            setErrorMessage('Please complete your address information in the General tab before registering with Athena Health.');
            return;
        }


        setIsLoading(true);
        setErrorMessage('');
        setSuccessMessage('');

        try {
            const response = await axios.post('/athena/customer/create', {
                name: userProfile.name,
                surname: userProfile.surname,
                dob: userProfile.dob,
                email: userProfile.email,
                phone: userProfile.phone,
                address: {
                    addressLine1: userProfile.location.addressLine1,
                    addressLine2: userProfile.location.addressLine2,
                    city: userProfile.location.city,
                    state: userProfile.location.state,
                    zipCode: userProfile.location.zipCode
                },
            }, {
                timeout: 45000 // 45 seconds timeout for Athena Health registration
            });
            const isMergeFlow = handleAthenaResponse(response);
            if (isMergeFlow) {
                setIsLoading(false);
                return;
            }

            setSuccessMessage('Successfully registered with Athena Health!');
            setTimeout(() => {
                navigate('/eligibility');
            }, 1500);
        } catch (error) {
            setErrorMessage(error.response?.data?.error || 'Failed to register with Athena Health');
        } finally {
            setIsLoading(false);
        }
    };

    const handleVerifyMerge = async () => {
        if (!otp) return;
        setIsVerifyingOtp(true);
        try {
            await axios.post('/athena/customer/confirm-merge', {
                code: otp,
                email: userProfile.email
            });

            setShowOtpModal(false);
            setSuccessMessage('Profile merged successfully!');
            setOtp('');

            setTimeout(() => {
                navigate('/eligibility');
            }, 1500);

            setTimeLeft(60)
            countdown(setTimeLeft)
            setIsOTPSend(true)
        } catch (error) {
            setErrorMessage(error.response?.data?.error || 'Invalid OTP. Please try again.');
        } finally {
            setIsVerifyingOtp(false);
        }
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-sky-50 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-sky-50">
            <div className="mx-auto pt-8 px-4 sm:px-6 lg:px-8" style={{ maxWidth: '1100px' }}>
                {/* Header Section */}
                <div className="text-center mb-8">
                    <h1 className="text-3xl font-bold tracking-tight bg-gradient-to-r from-blue-500 via-sky-500 to-sky-700 bg-clip-text text-transparent">
                        Athena Health Registration
                    </h1>
                    <p className="mt-2 text-sm text-gray-600">
                        Complete your profile information to register with Athena Health
                    </p>
                </div>

                {/* Info Section */}
                <div className="mb-8 bg-white rounded-2xl shadow-lg shadow-sky-900/5 ring-1 ring-slate-100 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Information from Your Profile</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div className="flex items-center space-x-3 text-gray-600">
                            <UserIcon className="w-5 h-5" />
                            <span>Name and Surname</span>
                        </div>
                        <div className="flex items-center space-x-3 text-gray-600">
                            <EnvelopeIcon className="w-5 h-5" />
                            <span>Email Address</span>
                        </div>
                        <div className="flex items-center space-x-3 text-gray-600">
                            <PhoneIcon className="w-5 h-5" />
                            <span>Phone Number</span>
                        </div>
                        <div className="flex items-center space-x-3 text-gray-600">
                            <CalendarIcon className="w-5 h-5" />
                            <span>Date of Birth</span>
                        </div>
                        <div className="flex items-center space-x-3 text-gray-600">
                            <MapPinIcon className="w-5 h-5" />
                            <span>Address Information</span>
                        </div>
                    </div>
                    <p className="mt-4 text-sm text-gray-500">
                        This information from your profile will be securely shared with Athena Health to create and manage your healthcare profile.
                    </p>
                </div>

                {/* Profile Completion Checklist */}
                <div className="bg-white rounded-2xl shadow-lg shadow-sky-900/5 ring-1 ring-slate-100 p-8">
                    <div className="mx-auto w-16 h-16 mb-6 rounded-full bg-blue-50 flex items-center justify-center">
                        <UserIcon className="w-8 h-8 text-blue-600" />
                    </div>

                    <h2 className="text-xl font-semibold text-gray-900 mb-6 text-center">
                        Profile Completion Checklist
                    </h2>

                    {successMessage && (
                        <div className="mb-6 p-4 bg-green-50 rounded-lg text-green-700 border border-green-200">
                            {successMessage}
                        </div>
                    )}

                    {errorMessage && (
                        <div className="mb-6 p-4 bg-red-50 rounded-lg text-red-700 border border-red-200">
                            {errorMessage}
                        </div>
                    )}

                    <div className="space-y-4 mb-8">
                        {requiredFields.map((field) => {
                            const Icon = field.icon;
                            const isComplete = isFieldComplete(field);

                            return (
                                <div key={field.key} className="flex items-center space-x-3 p-3 rounded-lg border">
                                    <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${isComplete ? 'bg-green-100' : 'bg-gray-100'
                                        }`}>
                                        {isComplete ? (
                                            <CheckIcon className="w-4 h-4 text-green-600" />
                                        ) : (
                                            <XMarkIcon className="w-4 h-4 text-gray-400" />
                                        )}
                                    </div>
                                    <Icon className="w-5 h-5 text-gray-400" />
                                    <span className={`flex-1 ${isComplete ? 'text-gray-900' : 'text-gray-600'}`}>
                                        {field.label}
                                        {field.required && <span className="text-red-500 ml-1">*</span>}
                                    </span>
                                    <span className={`text-sm ${isComplete ? 'text-green-600' : 'text-gray-400'}`}>
                                        {isComplete ? 'Complete' : 'Incomplete'}
                                    </span>
                                </div>
                            );
                        })}
                    </div>

                    <div className="text-center">
                        <p className="text-sm text-gray-600 mb-4">
                            Please complete all required fields (*) in the <strong>General</strong> tab before proceeding with Athena Health registration.
                        </p>
                        {/* Show registration button if not registered, update button if registered */}
                        {athenaProfile?.isRegistered ? (
                            <div className="space-y-4 flex flex-col items-center">
                                <button
                                    onClick={async () => {
                                        // Check if user has completed their profile
                                        if (!userProfile.name || !userProfile.surname || !userProfile.dob || !userProfile.email || !userProfile.phone) {
                                            setErrorMessage('Please complete your profile information in the General tab before updating Athena Health info.');
                                            return;
                                        }

                                        // Check if user has completed their address
                                        if (!userProfile.location?.city || !userProfile.location?.state || !userProfile.location?.zipCode) {
                                            setErrorMessage('Please complete your address information in the General tab before updating Athena Health info.');
                                            return;
                                        }

                                        setIsLoading(true);
                                        setErrorMessage('');
                                        setSuccessMessage('');
                                        try {
                                            await axios.put('/athena/customer/update', {
                                                name: userProfile.name,
                                                surname: userProfile.surname,
                                                dob: userProfile.dob,
                                                email: userProfile.email,
                                                phone: userProfile.phone,
                                                address: {
                                                    addressLine1: userProfile.location.addressLine1,
                                                    addressLine2: userProfile.location.addressLine2,
                                                    city: userProfile.location.city,
                                                    state: userProfile.location.state,
                                                    zipCode: userProfile.location.zipCode
                                                },
                                            }, {
                                                timeout: 45000 // 45 seconds timeout for Athena Health update
                                            });
                                            setSuccessMessage('Athena Health info updated successfully!');

                                            // Refresh Athena profile data
                                            // const athenaRes = await axios.get('/athena/patient/info');
                                            // setAthenaProfile(athenaRes.data[0]);
                                        } catch (error) {
                                            setErrorMessage(error.response?.data?.error || 'Failed to update Athena Health info');
                                        } finally {
                                            setIsLoading(false);
                                        }
                                    }}
                                    disabled={!userProfile.name || !userProfile.surname || !userProfile.dob || !userProfile.email || !userProfile.phone || !userProfile.location?.city || !userProfile.location?.state || !userProfile.location?.zipCode || isLoading}
                                    className="inline-flex items-center justify-center px-6 py-3 mt-4 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                                >
                                    {isLoading ? (
                                        <>
                                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Updating...
                                        </>
                                    ) : (
                                        'Update Athena Info'
                                    )}
                                </button>
                                <button
                                    onClick={() => navigate('/eligibility')}
                                    className="inline-flex items-center justify-center px-6 py-3 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-yellow-500 hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-400 transition-colors duration-200"
                                >
                                    Check Eligibility
                                </button>
                            </div>
                        ) : (
                            <button
                                onClick={handleRegisterWithAthena}
                                disabled={!userProfile.name || !userProfile.surname || !userProfile.dob || !userProfile.email || !userProfile.phone || !userProfile.location?.city || !userProfile.location?.state || !userProfile.location?.zipCode || isLoading}
                                className="inline-flex items-center justify-center px-6 py-3 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                            >
                                {isLoading ? (
                                    <>
                                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Registering...
                                    </>
                                ) : (
                                    'Complete Registration with Athena Health'
                                )}
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {showOtpModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto overflow-x-hidden bg-black bg-opacity-50 p-4 backdrop-blur-sm">
                    <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
                        <button
                            onClick={() => setShowOtpModal(false)}
                            className="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
                        >
                            <XMarkIcon className="h-6 w-6" />
                        </button>

                        <div className="flex flex-col items-center text-center">
                            <div className="mb-4 rounded-full bg-blue-100 p-3">
                                <LockClosedIcon className="h-8 w-8 text-blue-600" />
                            </div>
                            <h3 className="mb-2 text-xl font-bold text-gray-900">Verify Your Identity</h3>
                            <p className="mb-6 text-sm text-gray-500">
                                We found an existing profile. Please enter the verification code sent to your phone to link your accounts.
                            </p>

                            <ConfirmationCode setConfirm={setOtp}>
                                <p className="mt-4 text-sm text-gray-500">
                                    Didn't receive the code? <button className="text-blue-600 hover:underline" onClick={() => {
                                        // Resend OTP logic here
                                    }}>Resend</button>
                                </p>
                            </ConfirmationCode>

                            <button
                                onClick={handleVerifyMerge}
                                disabled={isVerifyingOtp || !otp}
                                className="w-full rounded-lg bg-blue-600 mt-5 px-5 py-2.5 text-center text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-300 disabled:opacity-50"
                            >
                                {isVerifyingOtp ? 'Verifying...' : 'Verify & Merge'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showSupportModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto overflow-x-hidden bg-black bg-opacity-50 p-4 backdrop-blur-sm">
                    <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
                        <button
                            onClick={() => setShowSupportModal(false)}
                            className="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
                        >
                            <XMarkIcon className="h-6 w-6" />
                        </button>

                        <div className="flex flex-col items-center text-center">
                            <div className="mb-4 rounded-full bg-yellow-100 p-3">
                                <InformationCircleIcon className="h-8 w-8 text-yellow-600" />
                            </div>
                            <h3 className="mb-2 text-xl font-bold text-gray-900">Action Required</h3>
                            <p className="mb-6 text-sm text-gray-500">
                                Additional assistance is required to finalize your account setup. Please call our support team to proceed.
                            </p>

                            <div className="mb-6 flex items-center justify-center space-x-2 rounded-lg bg-gray-50 px-6 py-3">
                                <PhoneIcon className="h-5 w-5 text-gray-600" />
                                <span className="text-lg font-semibold text-gray-900">{supportPhoneNumber}</span>
                            </div>

                            <button
                                onClick={() => setShowSupportModal(false)}
                                className="w-full rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-center text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-4 focus:ring-gray-200"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TabAthena; 